<template>
    <v-container class="mb-12">
        <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
                <v-card>
                    <v-card-title class="primary secondary--text d-block text-center">PAGAR CON WALLET</v-card-title>
                    <v-card-text>
                        <v-alert dense text type="info" class="mt-3">
                            El pago se realiza por nickname y se descuenta del saldo disponible.
                        </v-alert>
                        <v-form ref="form" v-model="valid" @submit.prevent="submit">
                            <v-autocomplete
                                v-if="contactItems.length"
                                v-model="selectedContactNickname"
                                :items="contactItems"
                                item-text="text"
                                item-value="value"
                                label="Favoritos wallet"
                                prepend-icon="mdi-star"
                                clearable
                                @change="selectContact"/>
                            <v-text-field
                                v-model="form.nickname"
                                label="Nickname destino"
                                prefix="$"
                                :rules="[rules.required]"
                                @blur="lookup"/>
                            <v-alert dense text type="success" v-if="recipient">
                                <div class="d-sm-flex align-center justify-space-between">
                                    <span>{{ recipient.name }} - {{ recipient.nickname }}</span>
                                    <v-btn
                                        v-if="!recipientIsContact"
                                        x-small
                                        text
                                        type="button"
                                        color="primary"
                                        :loading="contactSaving"
                                        @click="saveRecipientContact">
                                        <v-icon left small>mdi-star-plus</v-icon> Favorito
                                    </v-btn>
                                    <v-chip v-else x-small color="warning" text-color="white">
                                        <v-icon left small>mdi-star</v-icon> Favorito
                                    </v-chip>
                                </div>
                            </v-alert>
                            <v-checkbox
                                v-if="recipient && !recipientIsContact"
                                v-model="form.save_contact"
                                dense
                                hide-details
                                class="mt-n2 mb-2"
                                label="Guardar como favorito al pagar"/>
                            <v-select
                                v-model="form.currency"
                                :items="currencies"
                                item-text="code"
                                item-value="code"
                                label="Moneda"
                                :rules="[rules.required]"
                                @change="refreshAmountValidation"/>
                            <v-currency-field
                                ref="amountField"
                                v-model="form.amount"
                                label="Monto"
                                prepend-icon="mdi-cash-fast"
                                v-bind="currencyFieldConfig(form.currency)"
                                :rules="amountRules"
                                @input="refreshAmountValidation"/>
                            <v-alert dense text type="info" v-if="amountLimitText">
                                {{ amountLimitText }}
                            </v-alert>
                            <v-textarea
                                v-model="form.note"
                                label="Nota"
                                rows="2"/>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text to="/"><v-icon left>mdi-arrow-left</v-icon> Volver</v-btn>
                        <v-spacer/>
                        <v-btn color="primary" :loading="loading" :disabled="loading || !valid" @click="submit">
                            <v-icon left>mdi-send</v-icon> Pagar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import {
    currencyFieldConfig as moneyCurrencyFieldConfig,
    currencyMeta as moneyCurrencyMeta,
    parseMoney
} from '../../helpers/money';

export default {
    name: "WalletPay",
    data: () => ({
        valid: true,
        loading: false,
        recipient: null,
        contactSaving: false,
        currencies: [],
        allCurrencies: [],
        contacts: [],
        limits: {
            pay: {
                minimums: {},
                maximums: {}
            }
        },
        selectedContactNickname: null,
        form: {
            nickname: '',
            currency: 'USD',
            amount: null,
            note: '',
            save_contact: false
        },
        rules: {
            required: v => !!v || 'Campo obligatorio.'
        }
    }),
    created() {
        this.getWallet();
    },
    computed: {
        contactItems() {
            return (this.contacts || []).filter(contact => contact.is_favorite).map(contact => ({
                text: contact.label + ' - ' + contact.nickname,
                value: contact.raw_nickname || this.cleanNickname(contact.nickname),
                disabled: !contact.verified
            }));
        },
        recipientIsContact() {
            if (!this.recipient) {
                return false;
            }

            const nickname = this.cleanNickname(this.recipient.nickname);

            return (this.contacts || []).some(contact => this.cleanNickname(contact.nickname) === nickname);
        },
        amountRules() {
            return [this.rules.required, this.amountLimitRule];
        },
        amountLimitText() {
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (minimum && maximum) {
                return 'Monto permitido: ' + minimum.formatted + ' a ' + maximum.formatted + '.';
            }

            if (minimum) {
                return 'Monto minimo: ' + minimum.formatted + '.';
            }

            if (maximum) {
                return 'Monto maximo: ' + maximum.formatted + '.';
            }

            return '';
        }
    },
    methods: {
        getWallet() {
            axios.get('/api/wallet').then(res => {
                this.allCurrencies = res.data.currencies || [];
                this.currencies = this.balanceCurrencies(res.data.balances || [], res.data.wallet_currencies || []);
                this.contacts = res.data.contacts || [];
                this.limits = res.data.limits || this.limits;

                if (this.currencies.length && !this.currencies.some(item => item.code === this.form.currency)) {
                    this.form.currency = this.currencies[0].code;
                }
            });
        },
        balanceCurrencies(balances, walletCurrencies) {
            const currencies = (balances || []).map(balance => {
                const code = String(balance.currency || '').toUpperCase();
                const meta = (this.allCurrencies || []).find(item => item.code === code) || {};

                return Object.assign({}, meta, {
                    code: code,
                    label: balance.formatted || code
                });
            }).filter(item => !!item.code);

            return currencies.length ? currencies : (walletCurrencies || []);
        },
        cleanNickname(value) {
            return String(value || '').trim().replace(/^\$/, '').toUpperCase();
        },
        currencyMeta(code) {
            return moneyCurrencyMeta(this.currencies, code);
        },
        currencyFieldConfig(code) {
            return moneyCurrencyFieldConfig(this.currencySources(), code);
        },
        amountValue(value) {
            return parseMoney(value, this.currencySources(), this.form.currency);
        },
        currencySources() {
            return []
                .concat(this.currencies || [])
                .concat(this.allCurrencies || []);
        },
        amountLimit(kind) {
            const group = this.limits && this.limits.pay ? this.limits.pay : {};
            const key = kind === 'min' ? 'minimums' : 'maximums';
            const map = group[key] || {};

            return map[this.form.currency] || null;
        },
        amountLimitError(value) {
            const amount = this.amountValue(value !== undefined ? value : this.form.amount);
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (amount <= 0) {
                return '';
            }

            if (minimum && amount + 0.0001 < Number(minimum.amount)) {
                return 'El monto minimo es ' + minimum.formatted + '.';
            }

            if (maximum && amount - 0.0001 > Number(maximum.amount)) {
                return 'El monto maximo es ' + maximum.formatted + '.';
            }

            return '';
        },
        amountLimitRule(value) {
            return this.amountLimitError(value) || true;
        },
        refreshAmountValidation() {
            this.$nextTick(() => {
                const field = this.$refs.amountField;
                if (!field || typeof field.resetValidation !== 'function' || typeof field.validate !== 'function') {
                    return;
                }

                if (this.amountValue(this.form.amount) <= 0) {
                    field.resetValidation();
                    return;
                }

                field.validate(true);
            });
        },
        lookup() {
            this.recipient = null;
            this.form.nickname = this.cleanNickname(this.form.nickname);
            if (!this.form.nickname) return;

            axios.post('/api/wallet/recipient', {nickname: this.form.nickname}).then(res => {
                this.recipient = res.data.recipient;
                this.form.save_contact = !this.recipientIsContact && this.form.save_contact;
            }).catch(() => {});
        },
        selectContact(nickname) {
            if (!nickname) {
                return;
            }

            this.form.nickname = this.cleanNickname(nickname);
            this.form.save_contact = false;
            this.lookup();
        },
        saveRecipientContact() {
            const nickname = this.recipient ? this.recipient.nickname : this.form.nickname;

            if (!nickname || this.contactSaving) {
                return;
            }

            this.contactSaving = true;
            axios.post('/api/wallet/contacts', {
                nickname: nickname,
                alias: this.recipient ? this.recipient.name : null,
                is_favorite: true
            }).then(res => {
                this.mergeContact(res.data.contact);
                this.form.save_contact = false;
                this.$toast.success(res.data.status, {icon: 'mdi-star-check'});
            }).catch(err => {
                const msg = err.response && err.response.data && err.response.data.status ? err.response.data.status : 'No fue posible guardar el favorito.';
                this.$toast.error(msg, {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.contactSaving = false;
            });
        },
        mergeContact(contact) {
            if (!contact) {
                return;
            }

            const nickname = this.cleanNickname(contact.nickname);
            const index = (this.contacts || []).findIndex(item => this.cleanNickname(item.nickname) === nickname);

            if (index >= 0) {
                this.$set(this.contacts, index, contact);
            } else {
                this.contacts.unshift(contact);
            }

            this.selectedContactNickname = contact.raw_nickname || nickname;
        },
        submit() {
            if (!this.$refs.form.validate()) return;
            if (this.amountLimitError()) {
                this.refreshAmountValidation();
                this.$toast.warning(this.amountLimitError(), {icon: 'mdi-alert-circle'});
                return;
            }

            this.loading = true;
            axios.post('/api/wallet/pay', Object.assign({}, this.form, {
                nickname: this.cleanNickname(this.form.nickname),
                amount: this.amountValue(this.form.amount),
                save_contact: this.form.save_contact && !this.recipientIsContact,
                contact_alias: this.recipient ? this.recipient.name : null
            })).then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.mergeContact(res.data.contact);
                if (res.data.contact_warning) {
                    this.$toast.warning(res.data.contact_warning, {icon: 'mdi-information'});
                }
                this.$router.push('/');
            }).catch(err => {
                const msg = err.response && err.response.data && err.response.data.status ? err.response.data.status : 'No fue posible realizar el pago.';
                this.$toast.error(msg, {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        }
    }
}
</script>
